import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Building2, Mail, Phone, Globe, Scale, FileText } from 'lucide-react';
import { FlagImg } from '../components/FlagImg';

export const LegalNotice = () => {
  const { language, t } = useLanguage();

  const content = {
    en: {
      title: 'Legal Notice',
      subtitle: 'Corporate and legal information',
      lastUpdated: 'Last updated: February 2025',
      brandSection: {
        title: 'Brand Identity',
        text: 'TRH Steel is the commercial and corporate brand under which the following legal entities operate in the European steel industry:'
      },
      entities: {
        title: 'Legal Entities',
        spain: {
          title: 'TRH Sevilla – Spain',
          legalName: 'TRH ZF SEVILLA SOCIEDAD LIMITADA',
          commercialName: 'TRH ZF Sevilla SL',
          taxId: 'CIF/NIF: B90390345',
          address: 'Carretera de la Esclusa, s/n, Zona Franca Puerto de Sevilla, 41011 Sevilla, Spain',
          registry: 'Registered in the Mercantile Registry of Seville'
        },
        belgium: {
          title: 'TRH Liège – Belgium',
          legalName: 'TRH Liège SRL',
          commercialName: 'TRH Liège SRL',
          taxId: 'VAT/TVA: BE 0545.820.384',
          address: 'Rue de l\'Ile Monsin 107, BE-4020 Liège, Belgium',
          registry: 'Registered in the Crossroads Bank for Enterprises (BCE)'
        }
      },
      contact: {
        title: 'Contact Information',
        email: 'info@trhsteel.com',
        website: 'www.trhsteel.com'
      },
      ownership: {
        title: 'Website Ownership',
        text: 'This website (trhsteel.com) is owned and operated by TRH Steel through its legal entities mentioned above. All content, including text, graphics, logos, images, and software, is the property of TRH Steel or its licensors and is protected by intellectual property laws.'
      },
      jurisdiction: {
        title: 'Applicable Law and Jurisdiction',
        intro: 'The applicable law and jurisdiction depend on the contracting legal entity:',
        spain: 'For activities and contracts with TRH ZF Sevilla SL: Spanish law applies, with exclusive jurisdiction of the courts of Seville, Spain.',
        belgium: 'For activities and contracts with TRH Liège SRL: Belgian law applies, with exclusive jurisdiction of the courts of Liège, Belgium.',
        eu: 'Where applicable, European Union regulations shall take precedence in matters of consumer protection, data privacy, and cross-border trade.'
      },
      liability: {
        title: 'Limitation of Liability',
        text: 'TRH Steel makes reasonable efforts to ensure the accuracy of information on this website but does not warrant its completeness or accuracy. The information provided is for general purposes only and does not constitute professional advice. TRH Steel shall not be liable for any direct, indirect, or consequential damages arising from the use of this website.'
      },
      ip: {
        title: 'Intellectual Property',
        text: 'All trademarks, logos, and service marks displayed on this website are the property of TRH Steel or their respective owners. Unauthorized use, reproduction, or distribution of any content from this website is strictly prohibited without prior written consent.'
      }
    },
    es: {
      title: 'Aviso Legal',
      subtitle: 'Información corporativa y legal',
      lastUpdated: 'Última actualización: Febrero 2025',
      brandSection: {
        title: 'Identidad de Marca',
        text: 'TRH Steel es la marca comercial y corporativa bajo la cual operan las siguientes entidades legales en la industria siderúrgica europea:'
      },
      entities: {
        title: 'Entidades Legales',
        spain: {
          title: 'TRH Sevilla – España',
          legalName: 'TRH ZF SEVILLA SOCIEDAD LIMITADA',
          commercialName: 'TRH ZF Sevilla SL',
          taxId: 'CIF/NIF: B90390345',
          address: 'Carretera de la Esclusa, s/n, Zona Franca Puerto de Sevilla, 41011 Sevilla, España',
          registry: 'Inscrita en el Registro Mercantil de Sevilla'
        },
        belgium: {
          title: 'TRH Liège – Bélgica',
          legalName: 'TRH Liège SRL',
          commercialName: 'TRH Liège SRL',
          taxId: 'IVA/TVA: BE 0545.820.384',
          address: 'Rue de l\'Ile Monsin 107, BE-4020 Liège, Bélgica',
          registry: 'Inscrita en el Banco-Carrefour de Empresas (BCE)'
        }
      },
      contact: {
        title: 'Información de Contacto',
        email: 'info@trhsteel.com',
        website: 'www.trhsteel.com'
      },
      ownership: {
        title: 'Titularidad del Sitio Web',
        text: 'Este sitio web (trhsteel.com) es propiedad y está operado por TRH Steel a través de sus entidades legales mencionadas anteriormente. Todo el contenido, incluyendo textos, gráficos, logotipos, imágenes y software, es propiedad de TRH Steel o sus licenciantes y está protegido por las leyes de propiedad intelectual.'
      },
      jurisdiction: {
        title: 'Ley Aplicable y Jurisdicción',
        intro: 'La ley aplicable y la jurisdicción dependen de la entidad legal contratante:',
        spain: 'Para actividades y contratos con TRH ZF Sevilla SL: se aplica la ley española, con jurisdicción exclusiva de los tribunales de Sevilla, España.',
        belgium: 'Para actividades y contratos con TRH Liège SRL: se aplica la ley belga, con jurisdicción exclusiva de los tribunales de Lieja, Bélgica.',
        eu: 'Cuando corresponda, las regulaciones de la Unión Europea tendrán precedencia en materia de protección al consumidor, privacidad de datos y comercio transfronterizo.'
      },
      liability: {
        title: 'Limitación de Responsabilidad',
        text: 'TRH Steel realiza esfuerzos razonables para garantizar la precisión de la información en este sitio web, pero no garantiza su integridad o exactitud. La información proporcionada es solo para fines generales y no constituye asesoramiento profesional. TRH Steel no será responsable de daños directos, indirectos o consecuentes derivados del uso de este sitio web.'
      },
      ip: {
        title: 'Propiedad Intelectual',
        text: 'Todas las marcas comerciales, logotipos y marcas de servicio mostradas en este sitio web son propiedad de TRH Steel o sus respectivos propietarios. El uso, reproducción o distribución no autorizados de cualquier contenido de este sitio web está estrictamente prohibido sin consentimiento previo por escrito.'
      }
    },
    fr: {
      title: 'Mentions Légales',
      subtitle: 'Informations corporatives et légales',
      lastUpdated: 'Dernière mise à jour: Février 2025',
      brandSection: {
        title: 'Identité de Marque',
        text: 'TRH Steel est la marque commerciale et corporative sous laquelle opèrent les entités juridiques suivantes dans l\'industrie sidérurgique européenne:'
      },
      entities: {
        title: 'Entités Juridiques',
        spain: {
          title: 'TRH Sevilla – Espagne',
          legalName: 'TRH ZF SEVILLA SOCIEDAD LIMITADA',
          commercialName: 'TRH ZF Sevilla SL',
          taxId: 'CIF/NIF: B90390345',
          address: 'Polígono Industrial Torrecuéllar, 41011 Séville, Espagne',
          registry: 'Inscrite au Registre du Commerce de Séville'
        },
        belgium: {
          title: 'TRH Liège – Belgique',
          legalName: 'TRH Liège SRL',
          commercialName: 'TRH Liège SRL',
          taxId: 'TVA: BE 0545.820.384',
          address: 'Rue de l\'Ile Monsin 107, BE-4020 Liège, Belgique',
          registry: 'Inscrite à la Banque-Carrefour des Entreprises (BCE)'
        }
      },
      contact: {
        title: 'Coordonnées',
        email: 'info@trhsteel.com',
        website: 'www.trhsteel.com'
      },
      ownership: {
        title: 'Propriété du Site Web',
        text: 'Ce site web (trhsteel.com) est la propriété de TRH Steel et est exploité par ses entités juridiques mentionnées ci-dessus. Tout le contenu, y compris les textes, graphiques, logos, images et logiciels, est la propriété de TRH Steel ou de ses concédants et est protégé par les lois sur la propriété intellectuelle.'
      },
      jurisdiction: {
        title: 'Droit Applicable et Juridiction',
        intro: 'Le droit applicable et la juridiction dépendent de l\'entité juridique contractante:',
        spain: 'Pour les activités et contrats avec TRH ZF Sevilla SL: le droit espagnol s\'applique, avec compétence exclusive des tribunaux de Séville, Espagne.',
        belgium: 'Pour les activités et contrats avec TRH Liège SRL: le droit belge s\'applique, avec compétence exclusive des tribunaux de Liège, Belgique.',
        eu: 'Le cas échéant, les réglementations de l\'Union européenne prévaudront en matière de protection des consommateurs, de confidentialité des données et de commerce transfrontalier.'
      },
      liability: {
        title: 'Limitation de Responsabilité',
        text: 'TRH Steel fait des efforts raisonnables pour garantir l\'exactitude des informations sur ce site web mais ne garantit pas leur exhaustivité ou leur exactitude. Les informations fournies sont à titre informatif uniquement et ne constituent pas un conseil professionnel. TRH Steel ne sera pas responsable des dommages directs, indirects ou consécutifs résultant de l\'utilisation de ce site web.'
      },
      ip: {
        title: 'Propriété Intellectuelle',
        text: 'Toutes les marques, logos et marques de service affichés sur ce site web sont la propriété de TRH Steel ou de leurs propriétaires respectifs. L\'utilisation, la reproduction ou la distribution non autorisée de tout contenu de ce site web est strictement interdite sans consentement écrit préalable.'
      }
    },
    de: {
      title: 'Impressum',
      subtitle: 'Unternehmens- und Rechtsinformationen',
      lastUpdated: 'Letzte Aktualisierung: Februar 2025',
      brandSection: {
        title: 'Markenidentität',
        text: 'TRH Steel ist die Handels- und Unternehmensmarke, unter der folgende juristische Personen in der europäischen Stahlindustrie tätig sind:'
      },
      entities: {
        title: 'Juristische Personen',
        spain: {
          title: 'TRH Sevilla – Spanien',
          legalName: 'TRH ZF SEVILLA SOCIEDAD LIMITADA',
          commercialName: 'TRH ZF Sevilla SL',
          taxId: 'CIF/NIF: B90390345',
          address: 'Polígono Industrial Torrecuéllar, 41011 Sevilla, Spanien',
          registry: 'Eingetragen im Handelsregister von Sevilla'
        },
        belgium: {
          title: 'TRH Liège – Belgien',
          legalName: 'TRH Liège SRL',
          commercialName: 'TRH Liège SRL',
          taxId: 'USt-IdNr./TVA: BE 0545.820.384',
          address: 'Rue de l\'Ile Monsin 107, BE-4020 Lüttich, Belgien',
          registry: 'Eingetragen in der Zentrale Datenbank der Unternehmen (BCE)'
        }
      },
      contact: {
        title: 'Kontaktinformationen',
        email: 'info@trhsteel.com',
        website: 'www.trhsteel.com'
      },
      ownership: {
        title: 'Website-Eigentum',
        text: 'Diese Website (trhsteel.com) ist Eigentum von TRH Steel und wird von den oben genannten juristischen Personen betrieben. Alle Inhalte, einschließlich Texte, Grafiken, Logos, Bilder und Software, sind Eigentum von TRH Steel oder seiner Lizenzgeber und durch Gesetze zum Schutz des geistigen Eigentums geschützt.'
      },
      jurisdiction: {
        title: 'Anwendbares Recht und Gerichtsstand',
        intro: 'Das anwendbare Recht und der Gerichtsstand hängen von der vertragsschließenden juristischen Person ab:',
        spain: 'Für Aktivitäten und Verträge mit TRH ZF Sevilla SL: Es gilt spanisches Recht mit ausschließlichem Gerichtsstand Sevilla, Spanien.',
        belgium: 'Für Aktivitäten und Verträge mit TRH Liège SRL: Es gilt belgisches Recht mit ausschließlichem Gerichtsstand Lüttich, Belgien.',
        eu: 'Gegebenenfalls haben die Vorschriften der Europäischen Union Vorrang in Bezug auf Verbraucherschutz, Datenschutz und grenzüberschreitenden Handel.'
      },
      liability: {
        title: 'Haftungsbeschränkung',
        text: 'TRH Steel bemüht sich um die Richtigkeit der Informationen auf dieser Website, übernimmt jedoch keine Gewähr für deren Vollständigkeit oder Genauigkeit. Die bereitgestellten Informationen dienen nur allgemeinen Zwecken und stellen keine professionelle Beratung dar. TRH Steel haftet nicht für direkte, indirekte oder Folgeschäden, die sich aus der Nutzung dieser Website ergeben.'
      },
      ip: {
        title: 'Geistiges Eigentum',
        text: 'Alle auf dieser Website angezeigten Marken, Logos und Dienstleistungsmarken sind Eigentum von TRH Steel oder ihrer jeweiligen Inhaber. Die unbefugte Nutzung, Vervielfältigung oder Verbreitung von Inhalten dieser Website ist ohne vorherige schriftliche Zustimmung streng untersagt.'
      }
    },
    nl: {
      title: 'Juridische Kennisgeving',
      subtitle: 'Bedrijfs- en juridische informatie',
      lastUpdated: 'Laatst bijgewerkt: Februari 2025',
      brandSection: {
        title: 'Merkidentiteit',
        text: 'TRH Steel is het commerciële en bedrijfsmerk waaronder de volgende juridische entiteiten actief zijn in de Europese staalindustrie:'
      },
      entities: {
        title: 'Juridische Entiteiten',
        spain: {
          title: 'TRH Sevilla – Spanje',
          legalName: 'TRH ZF SEVILLA SOCIEDAD LIMITADA',
          commercialName: 'TRH ZF Sevilla SL',
          taxId: 'CIF/NIF: B90390345',
          address: 'Polígono Industrial Torrecuéllar, 41011 Sevilla, Spanje',
          registry: 'Ingeschreven in het Handelsregister van Sevilla'
        },
        belgium: {
          title: 'TRH Liège – België',
          legalName: 'TRH Liège SRL',
          commercialName: 'TRH Liège SRL',
          taxId: 'BTW: BE 0545.820.384',
          address: 'Rue de l\'Ile Monsin 107, BE-4020 Luik, België',
          registry: 'Ingeschreven in de Kruispuntbank van Ondernemingen (KBO)'
        }
      },
      contact: {
        title: 'Contactgegevens',
        email: 'info@trhsteel.com',
        website: 'www.trhsteel.com'
      },
      ownership: {
        title: 'Website Eigendom',
        text: 'Deze website (trhsteel.com) is eigendom van TRH Steel en wordt beheerd door de hierboven genoemde juridische entiteiten. Alle inhoud, inclusief teksten, afbeeldingen, logo\'s, afbeeldingen en software, is eigendom van TRH Steel of zijn licentiegevers en wordt beschermd door intellectuele eigendomsrechten.'
      },
      jurisdiction: {
        title: 'Toepasselijk Recht en Bevoegde Rechtbank',
        intro: 'Het toepasselijke recht en de bevoegde rechtbank zijn afhankelijk van de contracterende juridische entiteit:',
        spain: 'Voor activiteiten en contracten met TRH ZF Sevilla SL: Spaans recht is van toepassing, met exclusieve bevoegdheid van de rechtbanken van Sevilla, Spanje.',
        belgium: 'Voor activiteiten en contracten met TRH Liège SRL: Belgisch recht is van toepassing, met exclusieve bevoegdheid van de rechtbanken van Luik, België.',
        eu: 'Waar van toepassing, hebben de regelgeving van de Europese Unie voorrang op het gebied van consumentenbescherming, gegevensbescherming en grensoverschrijdende handel.'
      },
      liability: {
        title: 'Beperking van Aansprakelijkheid',
        text: 'TRH Steel doet redelijke inspanningen om de juistheid van de informatie op deze website te garanderen, maar garandeert niet de volledigheid of nauwkeurigheid ervan. De verstrekte informatie is uitsluitend bedoeld voor algemene doeleinden en vormt geen professioneel advies. TRH Steel is niet aansprakelijk voor directe, indirecte of gevolgschade voortvloeiend uit het gebruik van deze website.'
      },
      ip: {
        title: 'Intellectueel Eigendom',
        text: 'Alle handelsmerken, logo\'s en dienstmerken die op deze website worden weergegeven, zijn eigendom van TRH Steel of hun respectievelijke eigenaren. Ongeoorloofd gebruik, reproductie of verspreiding van enige inhoud van deze website is strikt verboden zonder voorafgaande schriftelijke toestemming.'
      }
    }
  };

  const c = content[language] || content.en;

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="legal-notice-page">
      {/* Hero */}
      <section className="py-16 md:py-20 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-3 mb-4">
            <Scale className="h-8 w-8 text-green-500" />
            <span className="text-green-500 text-sm uppercase tracking-wider font-bold">Legal</span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight mb-4">
            {c.title}
          </h1>
          <p className="text-slate-400">{c.subtitle}</p>
          <p className="text-slate-500 text-sm mt-4">{c.lastUpdated}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-slate-200 p-8 md:p-12 space-y-10">
            
            {/* Brand Section */}
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-4 flex items-center">
                <Globe className="h-5 w-5 mr-2 text-green-600" />
                {c.brandSection.title}
              </h2>
              <p className="text-slate-600 leading-relaxed">{c.brandSection.text}</p>
            </div>

            {/* Legal Entities */}
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-6 flex items-center">
                <Building2 className="h-5 w-5 mr-2 text-green-600" />
                {c.entities.title}
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Spain */}
                <div className="bg-slate-50 border border-slate-200 p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <FlagImg code="ES" size={28} />
                    <h3 className="font-heading text-lg font-bold text-slate-900 uppercase">
                      {c.entities.spain.title}
                    </h3>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p><strong>{t('legalName')}:</strong><br/>{c.entities.spain.legalName}</p>
                    <p><strong>{c.entities.spain.taxId}</strong></p>
                    <p className="text-slate-600">{c.entities.spain.address}</p>
                    <p className="text-slate-500 text-xs">{c.entities.spain.registry}</p>
                  </div>
                </div>

                {/* Belgium */}
                <div className="bg-slate-50 border border-slate-200 p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <FlagImg code="BE" size={28} />
                    <h3 className="font-heading text-lg font-bold text-slate-900 uppercase">
                      {c.entities.belgium.title}
                    </h3>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p><strong>{t('legalName')}:</strong><br/>{c.entities.belgium.legalName}</p>
                    <p><strong>{c.entities.belgium.taxId}</strong></p>
                    <p className="text-slate-600">{c.entities.belgium.address}</p>
                    <p className="text-slate-500 text-xs">{c.entities.belgium.registry}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact */}
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-4 flex items-center">
                <Mail className="h-5 w-5 mr-2 text-green-600" />
                {c.contact.title}
              </h2>
              <div className="flex flex-wrap gap-6 text-sm">
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4 text-slate-500" />
                  <a href={`mailto:${c.contact.email}`} className="text-slate-700 hover:text-green-600">{c.contact.email}</a>
                </div>
                <div className="flex items-center space-x-2">
                  <Globe className="h-4 w-4 text-slate-500" />
                  <span className="text-slate-700">{c.contact.website}</span>
                </div>
              </div>
            </div>

            {/* Website Ownership */}
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-4">
                {c.ownership.title}
              </h2>
              <p className="text-slate-600 leading-relaxed">{c.ownership.text}</p>
            </div>

            {/* Jurisdiction */}
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-4 flex items-center">
                <Scale className="h-5 w-5 mr-2 text-green-600" />
                {c.jurisdiction.title}
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">{c.jurisdiction.intro}</p>
              <ul className="space-y-3 text-sm text-slate-600">
                <li className="flex items-start">
                  <FlagImg code="ES" size={20} className="mr-2" />
                  <span>{c.jurisdiction.spain}</span>
                </li>
                <li className="flex items-start">
                  <FlagImg code="BE" size={20} className="mr-2" />
                  <span>{c.jurisdiction.belgium}</span>
                </li>
                <li className="flex items-start">
                  <FlagImg code="EU" size={20} className="mr-2" />
                  <span>{c.jurisdiction.eu}</span>
                </li>
              </ul>
            </div>

            {/* Limitation of Liability */}
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-4">
                {c.liability.title}
              </h2>
              <p className="text-slate-600 leading-relaxed">{c.liability.text}</p>
            </div>

            {/* Intellectual Property */}
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-4 flex items-center">
                <FileText className="h-5 w-5 mr-2 text-green-600" />
                {c.ip.title}
              </h2>
              <p className="text-slate-600 leading-relaxed">{c.ip.text}</p>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};
